import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type CreateEmployeeInput,
  type EmployeeDto,
  type PermissionKey,
  type PermissionSetDto,
  type UpdateEmployeeInput,
} from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { api } from '@/lib/api';
import { Field, ImageUpload, ghostPillClass, inputClass, pillClass } from './controls';
import { ownPrivilegesLocked } from './employeeSelfEdit';
import type { MediaIdEdit } from './gameLogic';
import { PermissionMatrix } from './PermissionMatrix';
import { PERMISSION_SETS_PATH, snapshotTemplate, type EmployeeTemplateLink } from './roleTemplates';
import { toFieldErrors, useResource, type FieldError } from './useSettings';

/**
 * Employee credentials + the privileges matrix (SPEC §6.4, §3).
 *
 * The matrix itself is the shared `PermissionMatrix` — the same catalogue the
 * API validates against and the Roles screen edits.
 *
 * TEMPLATES (wave 3): the picker above the matrix SNAPSHOTS the chosen role's
 * permissions into the matrix, client-side, immediately — what gets saved is
 * always the matrix the owner is looking at, never a reference the server
 * resolves later. Individual boxes stay editable after a snapshot, and the
 * link (`permissionSetId`) rides along with the save so the Roles screen can
 * offer its explicit re-sync.
 *
 * Owners hold every permission implicitly (§3), so their matrix is rendered
 * read-only rather than editable, and the template picker is hidden for them.
 */
export function EmployeeDrawer({
  employee,
  onClose,
  onSaved,
}: {
  employee: EmployeeDto | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { employee: signedIn } = useAuth();
  const isEdit = employee !== null;
  const isOwner = employee?.isOwner ?? false;
  // A manager on their own row: the grant, role and discount limit are the
  // owner's to set, and the API refuses a change to them (employeeSelfEdit.ts).
  const privilegesLocked = ownPrivilegesLocked(employee, signedIn);

  const [name, setName] = useState(employee?.name ?? '');
  const [phone, setPhone] = useState(employee?.phone ?? '');
  const [username, setUsername] = useState(employee?.username ?? '');
  const [password, setPassword] = useState('');
  const [isActive, setIsActive] = useState(employee?.isActive ?? true);
  // `undefined` = untouched, `null` = the trash icon, a string = a new upload.
  // See `MediaIdEdit`: two states could not say "remove the photo", so it
  // vanished from the preview and came back on reload.
  const [avatarMediaAssetId, setAvatarMediaAssetId] = useState<MediaIdEdit>(undefined);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(employee?.avatarUrl ?? null);
  const [granted, setGranted] = useState<Set<PermissionKey>>(new Set(employee?.permissions ?? []));
  const [permissionSetId, setPermissionSetId] = useState<string | null>(
    (employee as (EmployeeDto & EmployeeTemplateLink) | null)?.permissionSetId ?? null,
  );
  /** As typed — '' means "no limit". Parsed to number|null on save. */
  const [maxDiscountPercent, setMaxDiscountPercent] = useState<string>(
    employee?.maxDiscountPercent != null ? String(employee.maxDiscountPercent) : '',
  );

  // Owners never see the picker, so their drawer skips the request entirely.
  const templates = useResource<PermissionSetDto[]>(isOwner ? null : PERMISSION_SETS_PATH);

  const [errors, setErrors] = useState<FieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const errorFor = (field: string) => errors.find((issue) => issue.field === field)?.message;

  function chooseTemplate(id: string): void {
    if (!id) {
      // Detaching the link keeps the matrix as it stands — removing a template
      // must never silently revoke what staff can see is ticked.
      setPermissionSetId(null);
      return;
    }
    const template = (templates.data ?? []).find((entry) => entry.id === id);
    if (!template) return;
    setGranted(snapshotTemplate(template));
    setPermissionSetId(id);
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrors([]);
    setSubmitting(true);

    try {
      // '' = no limit. Sent as a number so zod's int/range rules apply — a
      // stray non-number becomes a field error rather than silently null.
      const discountLimit =
        maxDiscountPercent.trim() === '' ? null : Number(maxDiscountPercent.trim());

      if (isEdit && employee) {
        const payload: UpdateEmployeeInput & EmployeeTemplateLink = {
          name,
          phone: phone.trim() || null,
          username,
          isActive,
          // `!== undefined`, never truthiness: `null` is what clears the photo.
          ...(avatarMediaAssetId !== undefined ? { avatarMediaAssetId } : {}),
          // An owner's matrix is read-only; sending it would be rejected —
          // and owners are exempt from the discount limit, so it stays home too.
          // Your own row keeps all three home as well: they are the owner's.
          ...(isOwner || privilegesLocked ? {} : { permissions: [...granted], permissionSetId }),
          ...(isOwner || privilegesLocked ? {} : { maxDiscountPercent: discountLimit }),
        };
        await api.patch(`/employees/${employee.id}`, payload);
      } else {
        const payload: CreateEmployeeInput & EmployeeTemplateLink = {
          name,
          phone: phone.trim() || null,
          username,
          password,
          avatarMediaAssetId: avatarMediaAssetId ?? null,
          permissions: [...granted],
          permissionSetId,
          maxDiscountPercent: discountLimit,
        };
        await api.post('/employees', payload);
      }
      onSaved();
      onClose();
    } catch (caught) {
      setErrors(toFieldErrors(caught, t('settings.employees.saveFailed')));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="flex h-full w-full max-w-2xl flex-col border-s border-subtle bg-raised shadow-2xl"
      >
        <header className="border-b border-subtle p-5">
          <h2 className="text-lg font-semibold text-content">
            {isEdit ? t('settings.employees.editEmployee') : t('settings.employees.addNew')}
          </h2>
        </header>

        <div className="min-h-0 flex-1 overflow-auto p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <ImageUpload
                label={t('common.image')}
                kind="AVATAR"
                currentUrl={avatarUrl}
                onUploaded={(assetId, url) => {
                  setAvatarMediaAssetId(assetId);
                  setAvatarUrl(url);
                }}
                onCleared={() => {
                  setAvatarMediaAssetId(null);
                  setAvatarUrl(null);
                }}
              />
            </div>

            <Field label={t('common.name')} error={errorFor('name')}>
              {/* Plain text input — Arabic and Kurdish names must survive
                  verbatim, so nothing normalises or transliterates this. */}
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                className={inputClass}
              />
            </Field>

            <Field label={t('common.phoneNumber')} error={errorFor('phone')}>
              <input
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                dir="ltr"
                className={`${inputClass} tabular`}
              />
            </Field>

            <Field label={t('common.username')} error={errorFor('username')}>
              <input
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                required
                dir="ltr"
                autoComplete="off"
                className={inputClass}
              />
            </Field>

            {isEdit ? (
              <Field
                label={t('settings.employees.status')}
                hint={t('settings.employees.inactiveHint')}
              >
                <label className="flex items-center gap-2 rounded-control border border-subtle bg-input px-3 py-2">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(event) => setIsActive(event.target.checked)}
                    className="h-4 w-4 accent-[var(--brand-primary)]"
                  />
                  <span className="text-sm text-content">{t('settings.employees.active')}</span>
                </label>
              </Field>
            ) : (
              <Field label={t('common.password')} error={errorFor('password')}>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  autoComplete="new-password"
                  className={inputClass}
                />
              </Field>
            )}
          </div>

          {/* ---- Privileges matrix (SPEC §6.4) --------------------------- */}
          <section className="mt-6">
            <div className="mb-2 flex items-center gap-2">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-content-secondary">
                {t('settings.employees.privileges')}
              </h3>
              {isOwner ? (
                <span className="rounded-full bg-primary/20 px-2 py-0.5 text-xs text-primary">
                  {t('settings.employees.ownerHoldsAll')}
                </span>
              ) : null}
            </div>

            {privilegesLocked ? (
              <p data-own-privileges-locked className="mb-3 text-xs text-content-secondary">
                {t('employeeSelf.privilegesLocked')}
              </p>
            ) : null}

            {!isOwner ? (
              <div className="mb-3">
                <Field
                  label={t('settings.employees.template')}
                  hint={t('settings.employees.templateHint')}
                  error={errorFor('permissionSetId')}
                >
                  <select
                    value={permissionSetId ?? ''}
                    onChange={(event) => chooseTemplate(event.target.value)}
                    disabled={privilegesLocked}
                    className={inputClass}
                  >
                    <option value="">{t('settings.employees.templateNone')}</option>
                    {(templates.data ?? []).map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            ) : null}

            {!isOwner ? (
              <div className="mb-3">
                <Field
                  label={t('settings.employees.discountLimit')}
                  hint={t('settings.employees.discountLimitHint')}
                  error={errorFor('maxDiscountPercent')}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={maxDiscountPercent}
                      onChange={(event) => setMaxDiscountPercent(event.target.value)}
                      disabled={privilegesLocked}
                      placeholder={t('settings.employees.discountLimitNone')}
                      dir="ltr"
                      className={`${inputClass} tabular max-w-40`}
                    />
                    <span className="text-sm text-content-secondary">%</span>
                  </div>
                </Field>
              </div>
            ) : null}

            {errorFor('permissions') ? (
              <p className="mb-2 text-xs text-danger">{errorFor('permissions')}</p>
            ) : null}

            <PermissionMatrix
              granted={granted}
              disabled={isOwner || privilegesLocked}
              onChange={setGranted}
            />
          </section>
        </div>

        <footer className="border-t border-subtle p-5">
          {errorFor('(root)') ? (
            <p role="alert" className="mb-3 text-sm text-danger">
              {errorFor('(root)')}
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className={ghostPillClass}>
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={submitting} className={pillClass}>
              {t('common.save')}
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
}
